// Import the mixins
import { BugsnagService } from '@/mixins/services/bugsnag/BugsnagService'
import { SpinnerMethods } from '@/mixins/helpers/SpinnerMethods'
import { GenerateUID } from '@/mixins/helpers/GenerateUID'
import { GetFutureRentTerms } from '@/events/rent/GetFutureRentTerms'
import { GetCurrentRentTerms } from '@/events/rent/GetCurrentRentTerms'

// Import the repositories
import { RentRepository } from '@/repositories/rent/RentRepository'

// Import firebase
import { propertiesModel, residentsModel } from '@/firebase/firebase-config'

export const CreateRentTerm = {

    /*
	**  Register the mixins
	*/

	mixins: [

        BugsnagService,
        SpinnerMethods,
        GenerateUID,
        RentRepository,
        GetFutureRentTerms,
        GetCurrentRentTerms

    ],

    /*
	**  Set up the data
	*/

	data: () => ({

        new_rent_error: false,
        new_rent_saved: false,
        new_rent_complete: false,
        currentTotalBalance: 0,
        currentOutstandingBalance: 0,
        totalMonthlyRent: 0,
        totalFutureBalance: 0

    }),

    /*
    **  Watch em'
    */

    watch: {

        new_rent_saved: {

            immediate: true, // Load on created
            handler(new_rent_saved) {

                /*
                **  Update the lease
                **  @method: @here
                */

                if (new_rent_saved && !this.new_rent_error && this.new_rent.attributes.term == 'current') this.updateLeaseForNewRent();

                /*
                **  Update the resident
                **  @method: @here
                */

                if (new_rent_saved && !this.new_rent_error && this.new_rent.attributes.term == 'current') this.updateResidentForNewRent();

                /*
                **  Update the property resident
                **  @method: @here
                */

                if (new_rent_saved && !this.new_rent_error && this.new_rent.attributes.term == 'current') this.updatePropertyResidentForNewRent();

                /*
                **  Update the unit
                **  @method: @here
                */

                if (new_rent_saved && !this.new_rent_error && this.new_rent.attributes.term == 'current') this.updateUnitForNewRent();

                /*
                **  Update the property
                **  @method: @here
                */

                if (new_rent_saved && !this.new_rent_error && this.new_rent.attributes.term == 'current') this.updatePropertyForNewRent();

                /*
                **  Update the property
                **  @method: @here
                */

                if (new_rent_saved && !this.new_rent_error) this.getFutureRentTermsHandler(this.new_rent.relationships.property);
                if (new_rent_saved && !this.new_rent_error) this.getCurrentRentTermsHandler(this.new_rent.relationships.property);

                /*
                **  Set the completed prop
                **  @prop: @here
                */

                if (this.processing) this.new_rent_complete = true;

            }

        },

        new_rent_error: {

            immediate: true, // Load on created
            handler(new_rent_error) {

                /*
                **  Handle the error
                **  @method: @here
                */

                if (new_rent_error) this.handleNewRentError();

            }

        },

        new_rent_complete: {

            immediate: true, // Load on created
            handler(new_rent_complete) {

                /*
                **  Complete the transaction
                **  @method: @here
                */

                if (new_rent_complete) this.completeTheNewRentTransaction();

            }

        },

        current_rent_terms: {

            immediate: true, // Load on created
            handler(current_rent_terms) {

                /*
                **  Complete the transaction
                **  @method: @here
                */

                if (current_rent_terms.length > 0) this.updatePropertyCurrentMetricsForNewRent();

            }

        },

        future_rent_terms: {

            immediate: true, // Load on created
            handler(future_rent_terms) {

                /*
                **  Complete the transaction
                **  @method: @here
                */
                
                if (future_rent_terms.length > 0) this.updatePropertyFutureMetricsForNewRent();

            }

        }

    },

    /*
	**  Methods
    */
    
    methods: {

        createRentTermHandler: function() {

            /*
            **  Show the spinner
            **  @method: Mixins/Helpers/SpinnerMethods
            */

			this.showSpinner();

            /*
            **  Convert the dates
            **  @method: Mixins/Helpers/SpinnerMethods
            */

			this.convertNewRentDates()
            .then(() => {

                /*
                **  Set the rent status
                **  @method: @here
                */

				if (!this.new_rent_error && this.new_rent.attributes.term == 'current') this.setNewRentStatus();

            })
            .then(() => {

                /*
                **  Set the relationships
                **  @method: @here
                */

				if (!this.new_rent_error) this.setNewRentRelationships();

            })
            .then(() => {

                /*
                **  Convert amounts to cents
                **  @method: @here
                */

				if (!this.new_rent_error) this.convertNewRentAmounts();

            })
            .then(() => {

                /*
                **  Calculate total balance
                **  @method: @here
                */

				if (!this.new_rent_error) this.calculateNewRentTotalBalance();

            })
            .then(() => {

                /*
                **  Set the uid
                **  @method: Mixins/Helpers/GenerateUID
                */

				if (!this.new_rent_error) this.setNewRentUID();

            })
            .then(() => {

                /*
                **  Create the rent term
                **  @method: @here
                */

				if (!this.new_rent_error) this.createTheNewRentTerm();

            })
            .then(() => {

                /*
                **  If error, handle it
                **  @method: @here
                */

				if (this.new_rent_error) this.handleNewRentError();

            });

        },

        convertNewRentDates: async function() {

            try {

                /*
                **  Convert the dates
                **  @service: Repositories/Rent/RentRepository
                */

                this.new_rent.attributes.term_dates.starting_at = moment(this.new_rent.attributes.term_dates.starting_at).unix();
                this.new_rent.attributes.term_dates.ending_at = moment(this.new_rent.attributes.term_dates.ending_at).unix();

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Convert Dates When Creating a Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'X9NWKL9WkXIN0bD1VUCh',
                    error: e
                });

            }

        },

        setNewRentStatus: function() {

            try {

                /*
                **  Set the status
                **  @service: Repositories/Rent/RentRepository
                */

                this.new_rent.attributes.status = 'current';

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Set the New Rent Status When Creating a Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'pwT7rPpwDOyWz5vdNYu4',
                    error: e
                });

            }

        },

        setNewRentRelationships: function() {

            try {

                /*
                **  Set the relationship props
                **  @props: Repositories/Rent/RentRepository
                */

                this.new_rent.relationships.lease = this.lease.id;
                this.new_rent.relationships.property = this.property.id;
                this.new_rent.relationships.resident = this.resident.id;
                this.new_rent.relationships.unit = this.unit.id;

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Set the New Rent Relationships When Creating a Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: '0DmCHhXFKcOLC8cknWIG',
                    error: e
                });

            }

        },

        convertNewRentAmounts: function() {

            try {

                /*
                **  Convert the amounts
                **  @props: Repositories/Rent/RentRepository
                */

                this.new_rent.attributes.amount = Number(this.new_rent.attributes.amount) * 100;
                this.new_rent.attributes.amount = Number(this.new_rent.attributes.amount.toFixed(0));
                this.new_rent.attributes.first_month.charge_amount = Number(this.new_rent.attributes.first_month.charge_amount) * 100;
                this.new_rent.attributes.policy.late_fee = this.new_rent.attributes.policy.late_fee * 100;
                this.new_rent.attributes.policy.failed_payment_fee = Number(this.new_rent.attributes.policy.failed_payment_fee) * 100;
                
                /*
                **  If transaction fees are added
                **  @props: Repositories/Rent/RentRepository
                */

                if (this.new_rent.attributes.transaction_fees_policy.resident.fee_amount > 0) {

                    // Set the fee amount
                    this.new_rent.attributes.transaction_fees_policy.resident.fee_amount = Number(this.new_rent.attributes.transaction_fees_policy.resident.fee_amount) * 100;

                }

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Convert the New Rent Amounts When Creating a Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'k5QgOh5aiWrFnYJQqN0S',
                    error: e
                });

            }

        },

        calculateNewRentTotalBalance: function() {

            try {

                /*
                **  Set the total_balance
                **  @props: Repositories/Rent/RentRepository
                */

                if (this.new_rent.attributes.first_month.prorated) {

                    /*
                    **  Set the prorated balance
                    **  @props: Repositories/Rent/RentRepository
                    */

                    this.new_rent.metrics.total_balance = ((this.new_rent.metrics.total_charges - 1) * this.new_rent.attributes.amount) + this.new_rent.attributes.first_month.charge_amount;
                    this.new_rent.metrics.total_balance = Number(this.new_rent.metrics.total_balance.toFixed(0));
                
                } else {

                    /*
                    **  Set the non-prorated balance
                    **  @props: Repositories/Rent/RentRepository
                    */

                    this.new_rent.metrics.total_balance = this.new_rent.attributes.amount * this.new_rent.metrics.total_charges;
                    this.new_rent.metrics.total_balance = Number(this.new_rent.metrics.total_balance.toFixed(0));

                }

                /*
                **  Add transaction fee
                **  @props: Repositories/Rent/RentRepository
                */

                if (this.new_rent.attributes.transaction_fees_policy.method_type == 'resident') {

                    /*
                    **  Add to the balance
                    **  @props: Repositories/Rent/RentRepository
                    */

                    this.new_rent.metrics.total_balance = this.new_rent.metrics.total_balance + ((this.new_rent.metrics.total_charges - 1) * this.new_rent.attributes.transaction_fees_policy.resident.fee_amount);
                    this.new_rent.metrics.total_balance = Number(this.new_rent.metrics.total_balance.toFixed(0));

                }

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Calculate the New Rent Totals When Creating a Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'iqOx31RIvLsslUWgos8X',
                    error: e
                });

            }

        },

        setNewRentUID: function() {

            try {

                /*
                **  Set the prop
                **  @prop: Repositories/Rent/RentRepository
                */

                this.new_rent.id = this.generateUID();

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Set the New Rent UID When Creating a Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'cOryyP38KOZymR8pqotq',
                    error: e
                });

            }


        },

        createTheNewRentTerm: function() {

            try {

                propertiesModel.doc(this.property.id).collection('leases').doc(this.lease.id).collection('rents').doc(this.new_rent.id).set(this.new_rent)
                .then(() => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.new_rent_saved = true;

                })
                .catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.new_rent_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Unable to Set a New Rent Term in Firestore When Creating a Rent Term.',
                        errorType: 'Application',
                        section: 'Lease Overview',
                        uid: '7846BLGdcfRoEdEPm6R5',
                        error: error
                    });

                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Query Firestore When Creating a Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'MGtm6j2Kvr1GuLJBLyTD',
                    error: e
                });

            }

        },

        updateLeaseForNewRent: function() {

            try {

                /*
                **  Set the var
                */

                let firstMonthsRent = Number(this.new_rent.attributes.amount);

                /*
                **  Set first months rent
                **  @prop: Repositories/Rent/RentRepository
                */

                if (this.new_rent.attributes.first_month.prorated) firstMonthsRent = Number(this.new_rent.attributes.first_month.charge_amount);

                /*
                **  Update the lease
                **  @service: Firebase/Firestore
                */

                propertiesModel.doc(this.property.id).collection('leases').doc(this.new_rent.relationships.lease).update({

                    'attributes.rent.amount': Number(this.new_rent.attributes.amount),
                    'attributes.rent.beginning_at': this.new_rent.attributes.term_dates.starting_at,
                    'attributes.rent.cycle': this.new_rent.attributes.policy.cycle,
                    'attributes.rent.due_on': this.new_rent.attributes.policy.due_on,
                    'attributes.rent.first_months_rent': firstMonthsRent,
                    'attributes.rent.post_days_in_advance': this.new_rent.attributes.due_notification.days_in_advance,
                    'attributes.rent.prorated': this.new_rent.attributes.first_month.prorated,
                    'metrics.initial_balance': this.new_rent.metrics.total_balance,
                    'metrics.outstanding_balance': this.new_rent.metrics.total_balance,
                    'metrics.payments.payments_remaining': this.new_rent.metrics.total_charges,
                    'metrics.rent.total_charges': this.new_rent.metrics.total_charges,
                    'relationships.active_rent': this.new_rent.id,
                    'meta.updated_at': moment().unix()

                })
                .catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.new_rent_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Update Lease - Update Lease for New Rent in Firestore',
                        errorType: 'Firestore Database',
                        section: 'Lease Overview',
                        uid: 'RtVf9UbAFylGmtGrozrc',
                        error: error
                    });

                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Query Firestore When Updating a Lease for a New Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'Ic2yyViUj9istJO1XpUB',
                    error: e
                });

            }

        },

        updateResidentForNewRent: function() {

            try {

                residentsModel.doc(this.new_rent.relationships.resident).update({

                    'attributes.rent.current': Number(this.new_rent.attributes.amount)
    
                })
                .catch(error => {
                    
                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.new_rent_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Update Resident Current Rent in Firestore',
                        errorType: 'Firestore Database',
                        section: 'Lease Overview',
                        uid: 'RiAMnTwieGk5233t2mjZ',
                        error: error
                    });
    
                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Query Firestore When Updating a Resident for a New Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'vmnVIdsbBVYTRXqijJQp',
                    error: e
                });

            }

		},

        updatePropertyResidentForNewRent: function() {

            try {

                propertiesModel.doc(this.property.id).collection('residents').doc(this.resident.id).update({

                    'attributes.rent.current': this.new_rent.attributes.amount
    
                })
                .catch(error => {
    
                    // Send to bugsnag
                    this.reportAuthBugsnagError({
                        title: 'Update Property Resident Current Rent in Firestore',
                        errorType: 'Firestore Database',
                        section: 'Lease Overview',
                        uid: 'LsA8BrpflJS7compX9X0',
                        error: error
                    });
    
                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Query Firestore When Updating a Resident for a New Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'vmnVIdsbBVYTRXqijJQp',
                    error: e
                });

            }

		},

        updateUnitForNewRent: function() {

            try {

                propertiesModel.doc(this.property.id).collection('units').doc(this.new_rent.relationships.unit).update({

                    'attributes.rent.current': this.new_rent.attributes.amount,
                    'meta.updated_at': moment().unix()
    
                })
                .catch(error => {
    
                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.new_rent_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Update Property Unit Current Rent in Firestore',
                        errorType: 'Firestore Database',
                        section: 'Lease Overview',
                        uid: 'Oai7WsO1PKjS13LumD2Q',
                        error: error
                    });
    
                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Query Firestore When Updating a Unit for a New Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'WkY9ocbYAN5N1qwnxvsg',
                    error: e
                });

            }

		},

        updatePropertyForNewRent: function() {

            try {

                propertiesModel.doc(this.property.id).update({

                    'metrics.units.total_monthly_rent': this.property_clone.metrics.units.total_monthly_rent + this.new_rent.attributes.amount,
                    'meta.updated_at': moment().unix()
    
                })
                .catch(error => {
    
                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.new_rent_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Update Property Total Monthly Rent in Firestore',
                        errorType: 'Firestore Database',
                        section: 'Lease Overview',
                        uid: 'mIhjPV4HDtG4u2Vj2LZB',
                        error: error
                    });
    
                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Query Firestore When Updating a Property for a New Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'z2K32DY6U8Wmdb5DNCsy',
                    error: e
                });

            }

		},

        handleNewRentError: function() {

            try {

                /*
                **  Set the processing prop
                **  @prop: @here
                */

                this.processing = false;

                /*
                **  Reset the props
                **  @props: @here
                */

                this.new_rent_saved = false;
                this.new_rent_error = false;
                this.new_rent_complete = false;
                this.currentTotalBalance = 0;
                this.currentOutstandingBalance = 0;
                this.totalMonthlyRent = 0;
                this.totalFutureBalance = 0;
    
                /*
                **  Hide the spinner
                **  @method: Mixins/Helpers/SpinnerMethods
                */

                this.hideSpinner();

                /*
                **  Throw error message
                **  @service: SweetAlert2
                */

                this.$swal('Uh Oh!', 'We ran into some problems while trying to create your new rent term.', 'error');

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Handle an Error for a New Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: '7ePEs6uZ0ZPMDuNm3ikI',
                    error: e
                });

            }

        },

        completeTheNewRentTransaction: function() {

            try {

                /*
                **  Reset the new_rent prop
                **  @prop: Repositories/Rent/RentRepository
                */

                this.resetNewRentProp();

                /*
                **  Reset the props
                **  @props: @here
                */

                this.new_rent_saved = false;
                this.new_rent_error = false;
                this.new_rent_complete = false;
                this.currentTotalBalance = 0;
                this.currentOutstandingBalance = 0;
                this.totalMonthlyRent = 0;
                this.totalFutureBalance = 0;
 
                /*
                **  Set the processing prop
                **  @prop: Components/Property/Lease/Rent/AddRentMenu
                */

                this.processing = false;
 
                /*
                **  Close the menu
                **  @method: Components/Property/Lease/Rent/AddRentMenu
                */

                this.closeMenu();

                /*
                **  Hide the spinner
                **  @method: Mixins/Helpers/SpinnerMethods
                */

                this.hideSpinner();

                /*
                **  Throw success message
                **  @service: SweetAlert2
                */

                this.$swal('Success!', 'Your rent term has been created.', 'success');

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Complete the Transaction for a New Rent Term.',
                    errorType: 'Application',
                    section: 'Lease Overview',
                    uid: 'xNk6weTd2FHZAnOu4VyT',
                    error: e
                });

            }

        },

        updatePropertyCurrentMetricsForNewRent: function() {

            try {

                /*
                **  Loop through and total up the balance for the property
                */

                if (this.current_rent_terms.length > 0) {

                    this.current_rent_terms.forEach(rent => {

                        /*
                        **  Set the inidividual rent outstanding balance
                        */

                        let rentOutstandingBalance = rent.metrics.total_balance - rent.metrics.total_paid_amount;

                        /*
                        **  Update the vars
                        */

                        this.currentTotalBalance = this.currentTotalBalance + rent.metrics.total_balance;
                        this.currentOutstandingBalance = this.currentOutstandingBalance + rentOutstandingBalance;
                        this.totalMonthlyRent = this.totalMonthlyRent + rent.attributes.amount;

                    });

                } else {

                    /*
                    **  Update the vars
                    */

                    this.currentTotalBalance = 0;
                    this.currentOutstandingBalance = 0;
                    this.totalMonthlyRent = 0;

                }

                /*
                **  Update the document
                **  @service: @Firebase/Firestore
                */

                propertiesModel.doc(this.property.id).update({

                    'metrics.units.total_monthly_rent': this.totalMonthlyRent,
                    'metrics.leases.active.outstanding_balance': this.currentOutstandingBalance,
                    'metrics.leases.active.total_balance': this.currentTotalBalance,
                    'metrics.leases.future.total_balance': this.totalFutureBalance,
                    'meta.updated_at': moment().unix()
    
                })
                .catch(error => {
    
                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.new_rent_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Update Property Total Monthly Rent in Firestore',
                        errorType: 'Firestore Database',
                        section: 'New Rent',
                        uid: 'ZZtIbed7zrDbjHS5fByE',
                        error: error
                    });
    
                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Query Firestore When Updating a Property when Creating a Rent Term.',
                    errorType: 'Application',
                    section: 'New Rent',
                    uid: 'itYRgBXHOBunUsxFDWu0',
                    error: e
                });

            }

		},

        updatePropertyFutureMetricsForNewRent: function() {

            try {

                /*
                **  Loop through and total up the future balance for the property
                */

                if (this.future_rent_terms.length > 0) {

                    this.future_rent_terms.forEach(rent => {

                        this.totalFutureBalance = this.totalFutureBalance + rent.metrics.total_balance;

                    });

                } else {

                    /*
                    **  Update the vars
                    */

                    this.totalFutureBalance = 0;

                }

                /*
                **  Update the document
                **  @service: @Firebase/Firestore
                */

                propertiesModel.doc(this.property.id).update({

                    'metrics.units.total_monthly_rent': this.totalMonthlyRent,
                    'metrics.leases.active.outstanding_balance': this.currentOutstandingBalance,
                    'metrics.leases.active.total_balance': this.currentTotalBalance,
                    'meta.updated_at': moment().unix()
    
                })
                .catch(error => {
    
                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.new_rent_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Update Property Total Monthly Rent in Firestore',
                        errorType: 'Firestore Database',
                        section: 'New Rent',
                        uid: 'O0xXZezeL4sRf1Xhxd5m',
                        error: error
                    });
    
                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_rent_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Query Firestore When Updating a Property when creating a Rent Term.',
                    errorType: 'Application',
                    section: 'New Rent',
                    uid: 'BzfMdTnVT8pxuouGIDfO',
                    error: e
                });

            }

		},

    }

}